import { useEffect, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { FaPaperPlane } from 'react-icons/fa'
import { isFormStylingApplied, isButtonStylingApplied } from '../../utils/sectionFormStyling'

export default function ContactUsForm({ website, section, sectionFormStyling, useDefaultStyling = true, sectionType = 'contact', customerProfile = null, customerAuthToken = null }) {
  const [formData, setFormData] = useState({
    visitorName: '',
    visitorEmail: '',
    visitorPhone: '',
    subject: '',
    message: ''
  })
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [isFormHovered, setIsFormHovered] = useState(false)
  const [isButtonHovered, setIsButtonHovered] = useState(false)

  useEffect(() => {
    if (!customerProfile) return
    setFormData((prev) => ({
      ...prev,
      visitorName: prev.visitorName || `${customerProfile.firstName || ''} ${customerProfile.lastName || ''}`.trim(),
      visitorEmail: prev.visitorEmail || customerProfile.email || '',
      visitorPhone: prev.visitorPhone || customerProfile.phone || ''
    }))
  }, [customerProfile])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }))
  }

  const validateForm = () => {
    if (!formData.visitorName?.trim()) {
      toast.error('Name is required')
      return false
    }
    if (!formData.visitorEmail?.trim()) {
      toast.error('Email is required')
      return false
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.visitorEmail)) {
      toast.error('Please enter a valid email')
      return false
    }
    if (!formData.subject?.trim()) {
      toast.error('Subject is required')
      return false
    }
    if (!formData.message?.trim()) {
      toast.error('Message is required')
      return false
    }
    return true
  }

  const isFormStyling = useDefaultStyling || isFormStylingApplied(sectionFormStyling, sectionType, section?._id)
  const isButtonStyling = useDefaultStyling || isButtonStylingApplied(sectionFormStyling, sectionType, section?._id)
  
  // Default values to fallback to if styling is not provided
  const DEFAULT_FORM_COLORS = {
    backgroundColor: '#FFFFFF',
    textColor: '#111827',
    hoverBackgroundColor: '#F9FAFB',
    hoverTextColor: '#374151',
    buttonBackgroundColor: '#3B82F6',
    buttonTextColor: '#FFFFFF',
    hoverButtonBackgroundColor: '#2563eb',
    hoverButtonTextColor: '#ffffff'
  }
  
  // Ensure we have valid styling values
  const formColors = isFormStyling ? (sectionFormStyling || DEFAULT_FORM_COLORS) : undefined
  
  const formStyle = isFormStyling ? {
    backgroundColor: formColors?.backgroundColor || DEFAULT_FORM_COLORS.backgroundColor,
    color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor
  } : undefined
  const hoverFormStyle = isFormStyling ? {
    backgroundColor: formColors?.hoverBackgroundColor || formColors?.backgroundColor || DEFAULT_FORM_COLORS.hoverBackgroundColor,
    color: formColors?.hoverTextColor || formColors?.textColor || DEFAULT_FORM_COLORS.hoverTextColor
  } : undefined

  const labelStyle = isFormStyling ? { color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor } : undefined
  const hoverLabelStyle = isFormStyling ? { color: formColors?.hoverTextColor || formColors?.textColor || DEFAULT_FORM_COLORS.hoverTextColor } : undefined
  const textStyle = isFormStyling ? { color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor } : undefined
  const hoverTextStyle = isFormStyling ? { color: formColors?.hoverTextColor || formColors?.textColor || DEFAULT_FORM_COLORS.hoverTextColor } : undefined
  const inputStyle = isFormStyling ? { 
    backgroundColor: formColors?.backgroundColor || DEFAULT_FORM_COLORS.backgroundColor,
    color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor,
    borderColor: formColors?.textColor || DEFAULT_FORM_COLORS.textColor
  } : undefined

  const buttonStyle = isButtonStyling ? {
    backgroundColor: formColors?.buttonBackgroundColor || DEFAULT_FORM_COLORS.buttonBackgroundColor,
    color: formColors?.buttonTextColor || DEFAULT_FORM_COLORS.buttonTextColor
  } : { backgroundColor: '#3B82F6', color: '#FFFFFF' } // Always provide default button styling
  const hoverButtonStyle = isButtonStyling ? {
    backgroundColor: formColors?.hoverButtonBackgroundColor || formColors?.buttonBackgroundColor || DEFAULT_FORM_COLORS.hoverButtonBackgroundColor,
    color: formColors?.hoverButtonTextColor || formColors?.buttonTextColor || DEFAULT_FORM_COLORS.hoverButtonTextColor
  } : { backgroundColor: '#2563EB', color: '#FFFFFF' }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    setLoading(true)
    try {
      const response = await axios.post(
        '/contact-messages/submit',
        {
          websiteId: website._id,
          visitorName: formData.visitorName.trim(),
          visitorEmail: formData.visitorEmail.trim().toLowerCase(),
          visitorPhone: formData.visitorPhone.trim(),
          subject: formData.subject.trim(),
          message: formData.message.trim()
        },
        customerAuthToken ? { headers: { Authorization: `Bearer ${customerAuthToken}` } } : undefined
      )

      console.log('[ContactForm] Message submitted:', response.data)
      toast.success(response.data.message || 'Message sent successfully!')

      // Reset form
      setFormData({
        visitorName: '',
        visitorEmail: '',
        visitorPhone: '',
        subject: '',
        message: ''
      })

      setSubmitted(true)
      setTimeout(() => setSubmitted(false), 3000)
    } catch (error) {
      console.error('[ContactForm] Error sending message:', error)
      const errorMessage =
        error.response?.data?.message || 'Failed to send message. Please try again.'
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div 
      className="rounded-lg shadow-lg p-8 max-w-2xl mx-auto transition-colors duration-200" 
      style={isFormHovered && hoverFormStyle ? hoverFormStyle : formStyle}
      onMouseEnter={() => setIsFormHovered(true)}
      onMouseLeave={() => setIsFormHovered(false)}
    >
      <div className="mb-6">
        <h2 className="text-3xl font-bold flex items-center gap-2" style={textStyle}>
          <FaPaperPlane className="text-blue-600" />
          Contact Us
        </h2>
        <p className="mt-2" style={textStyle}>
          Have a question? We'd love to hear from you. Send us a message and we'll respond as
          soon as possible.
        </p>
      </div>

      {submitted && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-700 font-semibold">
            ✨ Thank you! Your message has been received. We'll get back to you soon!
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name */}
        <div>
          <label htmlFor="visitorName" className="block text-sm font-medium mb-1" style={labelStyle}>
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="visitorName"
            name="visitorName"
            value={formData.visitorName}
            onChange={handleChange}
            placeholder="John Doe"
            disabled={loading}
            style={inputStyle}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            required
          />
        </div>

        {/* Email */}
        <div>
          <label htmlFor="visitorEmail" className="block text-sm font-medium mb-1" style={labelStyle}>
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="visitorEmail"
            name="visitorEmail"
            value={formData.visitorEmail}
            onChange={handleChange}
            placeholder="john@example.com"
            disabled={loading}
            style={inputStyle}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            required
          />
        </div>

        {/* Phone (Optional) */}
        <div>
          <label htmlFor="visitorPhone" className="block text-sm font-medium mb-1" style={labelStyle}>
            Phone Number <span className="text-gray-500 text-xs">(Optional)</span>
          </label>
          <input
            type="tel"
            id="visitorPhone"
            name="visitorPhone"
            value={formData.visitorPhone}
            onChange={handleChange}
            placeholder="+1 (555) 123-4567"
            disabled={loading}
            style={inputStyle}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        </div>

        {/* Subject */}
        <div>
          <label htmlFor="subject" className="block text-sm font-medium mb-1" style={labelStyle}>
            Subject <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            placeholder="How can we help?"
            disabled={loading}
            style={inputStyle}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            required
          />
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium mb-1" style={labelStyle}>
            Message <span className="text-red-500">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us more about your inquiry..."
            disabled={loading}
            rows="5"
            style={inputStyle}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
            required
          />
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={loading}
            className="w-full hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center gap-2"
            style={isButtonHovered && hoverButtonStyle ? hoverButtonStyle : buttonStyle}
            onMouseEnter={() => setIsButtonHovered(true)}
            onMouseLeave={() => setIsButtonHovered(false)}
          >
            <FaPaperPlane />
            {loading ? 'Sending...' : 'Send Message'}
          </button>
        </div>
      </form>

      {/* Footer note */}
      <p className="text-xs text-center mt-6" style={textStyle}>
        We typically respond within 24-48 hours. Thank you for reaching out!
      </p>
    </div>
  )
}
